/**
 * Performance Optimization Constants
 *
 * Centralized constants for frontend performance tuning.
 * All values are configurable via environment variables.
 * Follows project mandate: 100% dynamic, no hardcoded values.
 *
 * @security SECURITY NOTE: Only non-sensitive configuration is cached.
 *           DSN, API keys, tokens, and secrets are NEVER cached.
 */

/**
 * Default values for performance tuning.
 * These are fallbacks ONLY when environment variables are not set.
 * All can be overridden via VITE_* environment variables.
 */
export const PERFORMANCE_DEFAULTS = {
    /** Default cache TTL in milliseconds (5 minutes) */
    CONFIG_CACHE_TTL_MS: 300000,
    /** Maximum wait time for service initialization in milliseconds */
    SERVICE_INIT_TIMEOUT_MS: 5000,
    /** Delay before initializing non-critical services (allows first paint) */
    DEFERRED_INIT_DELAY_MS: 100,
    /** Whether to enable configuration caching by default */
    ENABLE_CONFIG_CACHING: true,
    /** Default cache key */
    CONFIG_CACHE_KEY: 'sp_frontend_config_cache',
    /** Default cache version */
    CONFIG_CACHE_VERSION: 2,
    /** Delay for resetting download button state after a download triggered */
    RECEIPT_DOWNLOAD_RESET_DELAY_MS: 1000,
} as const;

/**
 * Environment variable keys for performance configuration.
 * These map to VITE_* environment variables.
 */
export const PERFORMANCE_ENV_KEYS = {
    CONFIG_CACHE_TTL_MS: 'VITE_CONFIG_CACHE_TTL_MS',
    SERVICE_INIT_TIMEOUT_MS: 'VITE_SERVICE_INIT_TIMEOUT_MS',
    ENABLE_CONFIG_CACHING: 'VITE_ENABLE_CONFIG_CACHING',
    DEFERRED_INIT_DELAY_MS: 'VITE_DEFERRED_INIT_DELAY_MS',
    CONFIG_CACHE_KEY: 'VITE_CONFIG_CACHE_KEY',
    CONFIG_CACHE_VERSION: 'VITE_CONFIG_CACHE_VERSION',
} as const;

/**
 * Service initialization event names.
 * Dispatched on window when services complete initialization.
 */
export const SERVICE_INIT_EVENTS = {
    CONFIG_READY: 'sp:config:ready',
    SENTRY_READY: 'sp:sentry:ready',
    TRACING_READY: 'sp:tracing:ready',
    ALL_SERVICES_READY: 'sp:services:ready',
} as const;

/**
 * Cache entry structure type definition.
 * Used for type-safe localStorage caching.
 */
export interface CacheEntry<T> {
    /** The cached data */
    data: T;
    /** Unix timestamp when cache was created */
    timestamp: number;
    /** Cache version for invalidation */
    version: number;
}

/**
 * Dashboard widget bootstrap time-slots.
 *
 * Each widget is assigned a discrete slot (N × staggerDelayMs) so that
 * initial API requests are spread across time instead of firing simultaneously,
 * preventing 429 rate-limit cascades on cold-boot.
 *
 * Slot 0 = priority lane (KPI cards — mounts immediately).
 * All stagger values are derived from the env-configurable stagger delay.
 *
 * @param staggerDelayMs - Value from ConfigurationService.getWidgetStaggerDelayMs()
 */
export const createWidgetSlots = (staggerDelayMs: number) => ({
    KPI_CARDS:     0,
    USER_STATS:    1 * staggerDelayMs,
    SYSTEM_HEALTH: 2 * staggerDelayMs,
    AUDIT_FEED:    3 * staggerDelayMs,
    SCHOLARSHIP:   4 * staggerDelayMs,
    SECURITY:      5 * staggerDelayMs,
    QUICK_ACTIONS: 6 * staggerDelayMs,
}) as const;

